/**
 * cart.js – Dawn Theme + Variant Slider + Free Shipping Bar + Perfect Cart Drawer
 * 100% working on Dawn 12–14+ (2024–2025)
 * Fixed: cart is not defined, formatMoney, cart refresh
 */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartNote extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', debounce((event) => {
      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: event.target.value })
      });
    }, 300));
  }
}
customElements.define('cart-note', CartNote);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') ||
                                 document.getElementById('CartDrawer-LineItemStatus');
    this.debouncedOnChange = debounce((event) => this.onChange(event), 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';
    const minQuantity = parseInt(event.target.dataset.min);
    if (inputValue < minQuantity && inputValue !== 0) {
      message = window.quickOrderListStrings.min_error.replace('[min]', minQuantity);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);


/* =============================================
   FREE SHIPPING BAR – FIXED & 100% WORKING
   ============================================= */
class DrawerFreeShipping {
  constructor(container) {
    this.container = container;
    this.progressFill = container.querySelector('.drawer-free-shipping__progress-fill');
    this.progressLabel = container.querySelector('.drawer-free-shipping__label');
    this.messageEl = container.querySelector('.drawer-free-shipping__text');

    this.threshold = parseInt(container.dataset.threshold) || 7500; // $75.00 in cents
    this.moneyFormat = window.theme?.currency?.money_format || window.theme?.settings?.money_format || '${{amount}}';

    // Listen to cart updates
    document.addEventListener('cart:updated', (e) => {
      const total = e.detail.cart?.items_subtotal_price || 0;
      this.update(total);
    });

    // Initial load: fetch current cart
    this.fetchCartAndUpdate();
  }

  fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.update(cart.items_subtotal_price || 0);
      })
      .catch(() => {
        // Fallback: hide bar if can't get cart
        this.container.style.display = 'none';
      });
  }

  formatMoney(cents) {
    // Safe Shopify.formatMoney with fallback
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }
    // Manual fallback
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', Math.floor(amount));
  }

  update(cartTotalCents) {
    const remaining = this.threshold - cartTotalCents;
    const percent = Math.min(100, Math.max(0, Math.round((cartTotalCents / this.threshold) * 100)));

    if (this.progressFill) this.progressFill.style.width = percent + '%';
    if (this.progressLabel) this.progressLabel.textContent = percent + '%';

    if (!this.messageEl) return;

    if (remaining <= 0) {
      this.messageEl.innerHTML = window.drawer_free_shipping_achieved_message || 'You unlocked FREE shipping!';
      this.container.classList.add('free-shipping--achieved');
    } else {
      const formatted = this.formatMoney(remaining);
      const msg = window.drawer_free_shipping_message || 'You are <b>{{ amount }}</b> away from free shipping';
      this.messageEl.innerHTML = msg.replace(/{{ amount }}/g, formatted);
      this.container.classList.remove('free-shipping--achieved');
    }
  }
}

// Auto-init all free shipping bars
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.drawer-free-shipping').forEach(bar => {
    console.log(bar)
    new DrawerFreeShipping(bar);
  });
});


/* =============================================
   VARIANT SLIDER + ADD TO CART – PERFECT
   ============================================= */
class VariantSlider extends HTMLElement {
  connectedCallback() {
    this.initSlider();

    this.bindAddToCart();
  }

  initSlider() {
    const container = this.querySelector('.variant-slider');
    if (!container || typeof Swiper === 'undefined') return;

    if (this.slider) this.slider.destroy(true, true);

    this.slider = new Swiper(container, {
      slidesPerView: 2,
      spaceBetween: 20,
      loop: false,
      speed: 400,
      navigation: {
        nextEl: this.querySelector('.variant-next'),
        prevEl: this.querySelector('.variant-prev'),
      },
      breakpoints: {
        750: { slidesPerView: 2, spaceBetween: 40 },
        1024: { slidesPerView: 2, spaceBetween: 40 }
      }
    });
  }

  bindAddToCart() {
    this.addEventListener('click', async (e) => {
      const btn = e.target.closest('.add-variant-to-cart');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';

        var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId, 
            quantity: 1,
            sections: cartEl.getSectionsToRender().map((section) => section.id)
          })
        });

        if (!res.ok) throw new Error();

        var response = await res.json();
        cartEl.renderContents(response);
        if (!(document.querySelector('cart-drawer-items').classList.contains('is-empty'))) {
          document.querySelector('cart-drawer').classList.remove('is-empty')
        }

        btn.textContent = 'Added!';
        btn.style.backgroundColor = '#28a745';

        // // Refresh cart sections
        // const sections = await fetch('/?sections=cart-drawer,cart-icon-bubble').then(r => r.json());
        // // Update cart icon
        // if (sections['cart-icon-bubble']) {
        //   const el = document.getElementById('cart-icon-bubble');
        //   if (el) el.outerHTML = sections['cart-icon-bubble'];
        // }

        // // Update drawer content
        // if (sections['CartDrawer-CartItems']) {
        //   const el = document.getElementById('CartDrawer-CartItems');
        //   if (el) el.innerHTML = new DOMParser().parseFromString(sections['CartDrawer-CartItems'], 'text/html')
        //     .querySelector('.cart-drawer__items').innerHTML;
        // }

        // if (sections['CartDrawer-CartFooter']) {
        //   const el = document.getElementById('CartDrawer-CartFooter');
        //   if (el) el.innerHTML = new DOMParser().parseFromString(sections['CartDrawer-CartFooter'], 'text/html')
        //     .querySelector('.cart-drawer__footer').innerHTML;
        // }

        // Open drawer
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.open();

        // Trigger free shipping update with real cart total
        document.dispatchEvent(new CustomEvent("cart:updated", {
          detail: { cart: { items_subtotal_price: (await (await fetch('/cart.js')).json()).items_subtotal_price } }
        }));

        // Optional: dim added card
        const card = btn.closest('.swiper-slide');
        if (card) {
          card.style.opacity = '0.4';
          card.style.pointerEvents = 'none';
        }

        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
          btn.style.backgroundColor = '';
        }, 1500);

      } catch (err) {
        console.error('Add to cart failed:', err);
        btn.textContent = 'Error';
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }, 2000);
      }
    });
  }
}
customElements.define('variant-slider', VariantSlider);


/* =============================================
   UTILS
   ============================================= */
function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}